import React, { useState } from 'react';

const EditorView = ({ people, capabilities, dataService, onDataChange, coverageCalculator }) => {
  const [activeTab, setActiveTab] = useState('people');
  const [editingPerson, setEditingPerson] = useState(null);
  const [editingCapability, setEditingCapability] = useState(null);
  // Removed unused newPersonForm state
  const [newCapabilityForm, setNewCapabilityForm] = useState({ name: '', description: '', required_skills: [], recommended_certifications: [] });

  const allSkills = coverageCalculator.getAllSkills(people, capabilities);
  const allCertifications = coverageCalculator.getAllCertifications(people, capabilities);

  const handleSavePerson = async (personData) => {
    try {
      await dataService.savePerson(personData);
      setEditingPerson(null);
      onDataChange();
    } catch (error) {
      console.error('Error saving person:', error);
    }
  };

  const handleSaveCapability = async () => {
    try {
      const updatedCapabilities = editingCapability && editingCapability !== 'new'
        ? capabilities.map(cap => cap.name === editingCapability ? newCapabilityForm : cap)
        : [...capabilities, newCapabilityForm];
      
      await dataService.saveCapabilities(updatedCapabilities);
      setEditingCapability(null);
      setNewCapabilityForm({ name: '', description: '', required_skills: [], recommended_certifications: [] });
      onDataChange();
    } catch (error) {
      console.error('Error saving capability:', error);
    }
  };

  const handleDeletePerson = async (personName) => {
    if (window.confirm(`Are you sure you want to delete ${personName}? This action cannot be undone.`)) {
      try {
        await dataService.deletePerson(personName);
        onDataChange();
      } catch (error) {
        console.error('Error deleting person:', error);
        alert('Error deleting person. Please try again.');
      }
    }
  };

  const PersonForm = ({ person, onSave, onCancel }) => {
    const [formData, setFormData] = useState(person || { name: '', skills: [], certifications: [], country: '' });
    const [newSkill, setNewSkill] = useState('');
    const [newCert, setNewCert] = useState('');

    const addSkill = () => {
      if (newSkill && !formData.skills.includes(newSkill)) {
        setFormData({ ...formData, skills: [...formData.skills, newSkill] });
        setNewSkill('');
      }
    };

    const removeSkill = (skill) => {
      setFormData({ ...formData, skills: formData.skills.filter(s => s !== skill) });
    };

    const addCertification = () => {
      if (newCert && !formData.certifications.includes(newCert)) {
        setFormData({ ...formData, certifications: [...formData.certifications, newCert] });
        setNewCert('');
      }
    };

    const removeCertification = (cert) => {
      setFormData({ ...formData, certifications: formData.certifications.filter(c => c !== cert) });
    };

    return (
      <div className="bg-gray-50 p-4 rounded-lg">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Country</label>
            <input
              type="text"
              value={formData.country || ''}
              onChange={(e) => setFormData({ ...formData, country: e.target.value })}
              placeholder="Enter country"
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Skills</label>
            <div className="mt-1 flex space-x-2">
              <input
                type="text"
                value={newSkill}
                onChange={(e) => setNewSkill(e.target.value)}
                placeholder="Add skill"
                className="flex-1 border border-gray-300 rounded-md px-3 py-2"
                list="skills-list"
              />
              <datalist id="skills-list">
                {allSkills.map(skill => (
                  <option key={skill} value={skill} />
                ))}
              </datalist>
              <button
                type="button"
                onClick={addSkill}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
              >
                Add
              </button>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {formData.skills.map(skill => (
                <span
                  key={skill}
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                >
                  {skill}
                  <button
                    type="button"
                    onClick={() => removeSkill(skill)}
                    className="ml-1 text-blue-600 hover:text-blue-800"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Certifications</label>
            <div className="mt-1 flex space-x-2">
              <input
                type="text"
                value={newCert}
                onChange={(e) => setNewCert(e.target.value)}
                placeholder="Add certification"
                className="flex-1 border border-gray-300 rounded-md px-3 py-2"
                list="certs-list"
              />
              <datalist id="certs-list">
                {allCertifications.map(cert => (
                  <option key={cert} value={cert} />
                ))}
              </datalist>
              <button
                type="button"
                onClick={addCertification}
                className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600"
              >
                Add
              </button>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {formData.certifications.map(cert => (
                <span
                  key={cert}
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"
                >
                  {cert}
                  <button
                    type="button"
                    onClick={() => removeCertification(cert)}
                    className="ml-1 text-green-600 hover:text-green-800"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>

          <div className="flex space-x-2">
            <button
              onClick={() => onSave(formData)}
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
            >
              Save
            </button>
            <button
              onClick={onCancel}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  };

  const CapabilityForm = () => {
    const [newSkill, setNewSkill] = useState('');
    const [newCert, setNewCert] = useState('');

    const addRequiredSkill = () => {
      if (newSkill && !newCapabilityForm.required_skills.includes(newSkill)) {
        setNewCapabilityForm({
          ...newCapabilityForm,
          required_skills: [...newCapabilityForm.required_skills, newSkill]
        });
        setNewSkill('');
      }
    };

    const removeRequiredSkill = (skill) => {
      setNewCapabilityForm({
        ...newCapabilityForm,
        required_skills: newCapabilityForm.required_skills.filter(s => s !== skill)
      });
    };

    const addRecommendedCert = () => {
      if (newCert && !newCapabilityForm.recommended_certifications.includes(newCert)) {
        setNewCapabilityForm({
          ...newCapabilityForm,
          recommended_certifications: [...newCapabilityForm.recommended_certifications, newCert]
        });
        setNewCert('');
      }
    };

    const removeRecommendedCert = (cert) => {
      setNewCapabilityForm({
        ...newCapabilityForm,
        recommended_certifications: newCapabilityForm.recommended_certifications.filter(c => c !== cert)
      });
    };

    return (
      <div className="bg-gray-50 p-4 rounded-lg">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Name</label>
            <input
              type="text"
              value={newCapabilityForm.name}
              onChange={(e) => setNewCapabilityForm({ ...newCapabilityForm, name: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Description</label>
            <textarea
              value={newCapabilityForm.description}
              onChange={(e) => setNewCapabilityForm({ ...newCapabilityForm, description: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
              rows="3"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Required Skills</label>
            <div className="mt-1 flex space-x-2">
              <input
                type="text"
                value={newSkill}
                onChange={(e) => setNewSkill(e.target.value)}
                placeholder="Add required skill"
                className="flex-1 border border-gray-300 rounded-md px-3 py-2"
                list="all-skills-list"
              />
              <datalist id="all-skills-list">
                {allSkills.map(skill => (
                  <option key={skill} value={skill} />
                ))}
              </datalist>
              <button
                type="button"
                onClick={addRequiredSkill}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
              >
                Add
              </button>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {newCapabilityForm.required_skills.map(skill => (
                <span
                  key={skill}
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                >
                  {skill}
                  <button
                    type="button"
                    onClick={() => removeRequiredSkill(skill)}
                    className="ml-1 text-blue-600 hover:text-blue-800"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Recommended Certifications</label>
            <div className="mt-1 flex space-x-2">
              <input
                type="text"
                value={newCert}
                onChange={(e) => setNewCert(e.target.value)}
                placeholder="Add recommended certification"
                className="flex-1 border border-gray-300 rounded-md px-3 py-2"
                list="all-certs-list"
              />
              <datalist id="all-certs-list">
                {allCertifications.map(cert => (
                  <option key={cert} value={cert} />
                ))}
              </datalist>
              <button
                type="button"
                onClick={addRecommendedCert}
                className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600"
              >
                Add
              </button>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {newCapabilityForm.recommended_certifications.map(cert => (
                <span
                  key={cert}
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"
                >
                  {cert}
                  <button
                    type="button"
                    onClick={() => removeRecommendedCert(cert)}
                    className="ml-1 text-green-600 hover:text-green-800"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>

          <div className="flex space-x-2">
            <button
              onClick={handleSaveCapability}
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
            >
              Save
            </button>
            <button
              onClick={() => {
                setEditingCapability(null);
                setNewCapabilityForm({ name: '', description: '', required_skills: [], recommended_certifications: [] });
              }}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-medium text-gray-900">Editor</h2>
        <div className="mt-4">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('people')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'people'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              People
            </button>
            <button
              onClick={() => setActiveTab('capabilities')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'capabilities'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Capabilities
            </button>
          </nav>
        </div>
      </div>

      <div className="p-6">
        {activeTab === 'people' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">People</h3>
              <button
                onClick={() => setEditingPerson('new')}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
              >
                Add Person
              </button>
            </div>

            {editingPerson === 'new' && (
              <PersonForm
                onSave={handleSavePerson}
                onCancel={() => setEditingPerson(null)}
              />
            )}

            <div className="grid gap-4">
              {people.map((person, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  {editingPerson === person.name ? (
                    <PersonForm
                      person={person}
                      onSave={handleSavePerson}
                      onCancel={() => setEditingPerson(null)}
                    />
                  ) : (
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-medium text-gray-900">{person.name}</h4>
                        <div className="mt-2 space-y-1">
                          <div>
                            <span className="text-sm text-gray-500">Skills: </span>
                            <span className="text-sm">{person.skills?.join(', ') || 'None'}</span>
                          </div>
                          <div>
                            <span className="text-sm text-gray-500">Certifications: </span>
                            <span className="text-sm">{person.certifications?.join(', ') || 'None'}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => setEditingPerson(person.name)}
                          className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeletePerson(person.name)}
                          className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'capabilities' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">Capabilities</h3>
              <button
                onClick={() => setEditingCapability('new')}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
              >
                Add Capability
              </button>
            </div>

            {editingCapability === 'new' && <CapabilityForm />}

            <div className="grid gap-4">
              {capabilities.map((capability, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  {editingCapability === capability.name ? (
                    <CapabilityForm />
                  ) : (
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-medium text-gray-900">{capability.name}</h4>
                        {capability.description && (
                          <p className="text-sm text-gray-600 mt-1">{capability.description}</p>
                        )}
                        <div className="mt-2 space-y-1">
                          <div>
                            <span className="text-sm text-gray-500">Required Skills: </span>
                            <span className="text-sm">{capability.required_skills?.join(', ') || 'None'}</span>
                          </div>
                          <div>
                            <span className="text-sm text-gray-500">Recommended Certifications: </span>
                            <span className="text-sm">{capability.recommended_certifications?.join(', ') || 'None'}</span>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          setEditingCapability(capability.name);
                          setNewCapabilityForm(capability);
                        }}
                        className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                      >
                        Edit
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditorView;